import PhotoGrayIcon from '@assets/images/common/image_dummy.svg';
import { Article } from '@src/api/types/article.ts';
import { SnsShareButtons } from '@src/components/SnsShareButtons';
import { useProcessedTableContent } from '@src/hooks/useProcessedContent';
import Tag from '@src/ui/elements/Company/Tag';
import { Link } from 'react-router-dom';

import ArticleItem from '../company/articles/components/ArticleItem';

type Props = {
  article: Article;
  relatedArticles: Article[];
  shareData: {
    url: string;
    title: string;
    tags: string[];
  } | null;
};

const ArticleContent = ({ article, relatedArticles, shareData }: Props) => {
  const processedContent = useProcessedTableContent(article?.content || '');

  return (
    <>
      <div className='lg:mt-10'>
        <style>{`
          [data-article-intro-grid] {
            grid-template-areas: 'title title' 'meta pickup' 'tag tag';
          }
          @media (max-width: 1024px) {
            [data-article-intro-grid] {
              grid-template-areas: 'meta' 'title' 'tag' 'pickup';
            }
          }
          @media (max-width: 768px) {
            .tiptap-content table {
              width: 100% !important;
              font-size: 14px;
            }
            .tiptap-content table th,
            .tiptap-content table td {
              padding: 8px 4px !important;
              word-break: break-word;
            }
          }
        `}</style>
        <div
          className='grid grid-cols-[1fr_auto] gap-6 lg:grid-cols-[100%]'
          data-article-intro-grid
        >
          <h1
            className='break-all text-3xl font-bold leading-normal md:text-xl'
            style={{
              gridArea: 'title',
            }}
          >
            {article?.title}
          </h1>
          <div
            className='flex items-center gap-4 md:justify-between'
            style={{
              gridArea: 'meta',
            }}
          >
            <Link to={`/articles?category_slug=${article.category?.slug}`}>
              <p className='line-clamp-1 border-b-2 border-solid border-b-primary py-1 text-[15px] text-primary'>
                {article.category?.name}
              </p>
            </Link>
            <time
              className='whitespace-nowrap text-[14px]'
              dateTime={article.published_at}
            >
              {article.published_at}
            </time>
          </div>
          <div
            style={{ gridArea: 'pickup' }}
            className='pickup-container'
          >
            {article.is_pickup && <span className='text-primary'>ピックアップ記事</span>}

            <div className='pickup-share-buttons'>
              {article.is_pickup && shareData && (
                <SnsShareButtons
                  url={shareData.url}
                  title={shareData.title}
                  tags={shareData.tags}
                  variant='compact'
                />
              )}
            </div>
          </div>
          <ul
            className='flex flex-wrap gap-2'
            style={{
              gridArea: 'tag',
            }}
          >
            {article.tags?.map((tag, index) => (
              <li key={index}>
                <Tag
                  to={`/articles?tag_slug=${tag.slug}`}
                  label={tag.name}
                />
              </li>
            ))}
          </ul>
        </div>
        <div
          className='tiptap-content mt-12 overflow-hidden'
          dangerouslySetInnerHTML={{
            __html: processedContent,
          }}
        />
        {article?.original_pdf_name && (
          <div className='mt-12'>
            <a
              href={article?.pdf_url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-base text-primary hover:underline'
            >
              {article.original_pdf_name}
            </a>
          </div>
        )}
        {shareData && (
          <div className='mt-10 px-6'>
            <SnsShareButtons
              url={shareData.url}
              title={shareData.title}
              tags={shareData.tags}
              variant='full'
            />
          </div>
        )}
      </div>
      <div className='mt-6 border-t border-solid border-t-gray-200 py-12 lg:mt-2'>
        <h2 className='text-lg font-semibold md:text-sm'>関連記事</h2>
        <div className='mt-10 grid grid-cols-3 gap-x-8 gap-y-10 md:mt-8 md:grid-cols-1'>
          {relatedArticles.map((article, index) => (
            <ArticleItem
              key={index}
              to={`/articles/${article.id}`}
              title={article.title}
              content={article.content}
              hideMobileDescription
              date={article.published_at}
              category={{
                id: article.category?.id ?? 0,
                name: article.category?.name ?? '',
                slug: article.category?.slug ?? '',
              }}
              tags={
                article.tags?.map((tag) => ({
                  id: tag.id,
                  name: tag.name,
                  slug: tag.slug,
                })) ?? []
              }
              thumbnail={article.thumbnail_url || PhotoGrayIcon}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default ArticleContent;
